import React, { useState, useEffect, useRef } from 'react'
import { API_BASE_URL } from '../config/api';
import { Download, Trash2, Eye, Send, FileText, AlertCircle, Search, Filter, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import BotonVisualizar from '../components/BotonVisualizar'
import { puedeAbrirRuta } from '../utils/accesoRutas'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 350

const HistorialGeneral = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [signatures, setSignatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [estadosCount, setEstadosCount] = useState({})
  const [claveCargada, setClaveCargada] = useState('')
  const solicitud = useRef(0)
  const [users, setUsers] = useState([]) // Lista de usuarios para el dropdown
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [selectedDocuments, setSelectedDocuments] = useState([]) // IDs de documentos seleccionados
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false) // Modal para eliminación masiva
  const [documentoToDelete, setDocumentoToDelete] = useState(null)
  const [showPropietarioSuggestions, setShowPropietarioSuggestions] = useState(false) // Mostrar sugerencias de propietario
  const [propietarioInputFocused, setPropietarioInputFocused] = useState(false)
  
  const isAdmin = puedeAbrirRuta('/historial-general', user)
  
  // Filtros
  const [filters, setFilters] = useState({
    documento: '', // Búsqueda por título/nombre del documento
    propietario: '' // Email del propietario seleccionado
  })

  const filtersKey = `${filters.documento}|${filters.propietario}`
  const filtrosPrevios = useRef(filtersKey)

  useEffect(() => {
    if (!isAdmin) {
      toast.error('No tienes permisos para acceder a esta página')
      navigate('/dashboard')
      return
    }
    loadUsers()
  }, [isAdmin, navigate])

  useEffect(() => {
    if (!isAdmin) return undefined
    const filtrosCambiaron = filtrosPrevios.current !== filtersKey
    filtrosPrevios.current = filtersKey
    const espera = filtrosCambiaron ? SEARCH_DEBOUNCE_MS : 0
    const timer = setTimeout(() => {
      loadSignatures(page, filters)
    }, espera)
    return () => clearTimeout(timer)
  }, [isAdmin, page, filtersKey])

  useEffect(() => {
    setSelectedDocuments([])
  }, [page, filtersKey])

  useEffect(() => {
    if (loading) return
    window.dispatchEvent(new Event('scroll'))
  }, [loading, total])

  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // Ordenar usuarios por email
        const sortedUsers = data.sort((a, b) => {
          const emailA = (a.email || '').toLowerCase()
          const emailB = (b.email || '').toLowerCase()
          return emailA.localeCompare(emailB)
        })
        setUsers(sortedUsers)
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const loadSignatures = async (pageNumber = page, filtrosActivos = filters) => {
    const idSolicitud = ++solicitud.current
    const clave = `${pageNumber}|${filtrosActivos.documento}|${filtrosActivos.propietario}`
    try {
      setLoading(true)
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        toast.error('No hay token de autenticación')
        return
      }

      const params = new URLSearchParams()
      params.set('page', String(pageNumber))
      params.set('page_size', String(PAGE_SIZE))
      if (filtrosActivos.documento?.trim()) params.set('search', filtrosActivos.documento.trim())
      if (filtrosActivos.propietario?.trim()) params.set('propietario', filtrosActivos.propietario.trim())

      const response = await fetch(`${API_BASE_URL}/api/documentos/historial?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
          localStorage.removeItem('token')
          sessionStorage.removeItem('token')
          setTimeout(() => navigate('/login'), 1500)
          return
        }
        throw new Error('Error al cargar historial')
      }
      
      const data = await response.json()
      if (idSolicitud !== solicitud.current) return
      const items = Array.isArray(data) ? data : (data.items || [])
      setSignatures(items)
      setTotal(Array.isArray(data) ? items.length : (data.total ?? items.length))
      setTotalPages(Array.isArray(data) ? 1 : (data.total_pages ?? 0))
      setEstadosCount(Array.isArray(data) ? {} : (data.estados || {}))
      setClaveCargada(clave)
    } catch (error) {
      if (idSolicitud !== solicitud.current) return
      setSignatures([])
      setTotal(0)
      setTotalPages(0)
      setEstadosCount({})
      setClaveCargada(clave)
      toast.error('Error al cargar el historial: ' + error.message)
    } finally {
      if (idSolicitud === solicitud.current) setLoading(false)
    }
  }
  
  const handleFilterChange = (key, value) => {
    setPage(1)
    setFilters(prev => ({ ...prev, [key]: value }))
    // Mostrar sugerencias cuando se escribe en propietario
    if (key === 'propietario') {
      setShowPropietarioSuggestions(value.trim().length > 0 && propietarioInputFocused)
    }
  }

  // Filtrar usuarios según lo que se escribe
  const getFilteredUsers = () => {
    if (!filters.propietario || filters.propietario.trim().length === 0) {
      return users
    }
    const searchTerm = filters.propietario.trim().toLowerCase()
    return users.filter(user => {
      const email = (user.email || '').toLowerCase()
      const nombre = (user.nombre || '').toLowerCase()
      const apellido = (user.apellido || '').toLowerCase()
      const nombreCompleto = `${nombre} ${apellido}`.trim().toLowerCase()
      
      return email.includes(searchTerm) || 
             nombre.includes(searchTerm) || 
             apellido.includes(searchTerm) ||
             nombreCompleto.includes(searchTerm)
    })
  }

  const handleSelectPropietario = (userEmail) => {
    setPage(1)
    setFilters(prev => ({ ...prev, propietario: userEmail }))
    setShowPropietarioSuggestions(false)
    setPropietarioInputFocused(false)
  }
  
  const clearFilters = () => {
    setPage(1)
    setFilters({
      documento: '',
      propietario: ''
    })
  }
  
  const hasActiveFilters = filters.documento.trim() || filters.propietario.trim()

  const abrirModalEliminar = (documentoId, estado) => {
    // Admin puede eliminar CUALQUIER documento
    setDocumentoToDelete({ id: documentoId, estado })
    setShowDeleteModal(true)
  }

  const eliminarDocumento = async () => {
    if (!documentoToDelete) return

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        toast.error('No hay token de autenticación')
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/documentos/${documentoToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }))
        throw new Error(errorData.detail || 'Error al eliminar documento')
      }

      toast.success('Documento eliminado exitosamente')
      setShowDeleteModal(false)
      setDocumentoToDelete(null)
      loadSignatures() // Recargar lista
    } catch (error) {
      toast.error('Error al eliminar documento: ' + error.message)
    }
  }

  const handleSelectDocument = (documentoId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentoId)) {
        return prev.filter(id => id !== documentoId)
      } else {
        return [...prev, documentoId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedDocuments.length === signatures.length) {
      setSelectedDocuments([])
    } else {
      setSelectedDocuments(signatures.map(doc => doc.id))
    }
  }

  const abrirModalEliminarMasivo = () => {
    if (selectedDocuments.length === 0) {
      toast.error('Por favor selecciona al menos un documento para eliminar')
      return
    }
    setShowBulkDeleteModal(true)
  }

  const eliminarDocumentosMasivo = async () => {
    if (selectedDocuments.length === 0) return

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        toast.error('No hay token de autenticación')
        return
      }

      // Eliminar documentos uno por uno
      let successCount = 0
      let errorCount = 0
      const errors = []

      for (const documentoId of selectedDocuments) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/documentos/${documentoId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (response.ok) {
            successCount++
          } else {
            errorCount++
            const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }))
            errors.push(`Documento ${documentoId}: ${errorData.detail || 'Error desconocido'}`)
          }
        } catch (error) {
          errorCount++
          errors.push(`Documento ${documentoId}: ${error.message}`)
        }
      }

      // Mostrar resultados
      if (successCount > 0) {
        toast.success(`${successCount} documento(s) eliminado(s) exitosamente`)
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} documento(s) no pudieron ser eliminados`)
        console.error('Errores al eliminar:', errors)
      }

      setShowBulkDeleteModal(false)
      setSelectedDocuments([])
      loadSignatures() // Recargar lista
    } catch (error) {
      toast.error('Error al eliminar documentos: ' + error.message)
      setShowBulkDeleteModal(false)
    }
  }

  const descargarDocumento = async (documentoId, rutaPdfFirmado, rutaPdfOriginal, estado) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (!token) {
        toast.error('No hay token de autenticación')
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/documentos/${documentoId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
          localStorage.removeItem('token')
          sessionStorage.removeItem('token')
          setTimeout(() => navigate('/login'), 1500)
          return
        }
        if (response.status === 404) {
          throw new Error('Archivo PDF no encontrado')
        }
        const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }))
        throw new Error(errorData.detail || 'Error al descargar documento')
      }

      // Obtener el nombre del archivo del header Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `documento_${documentoId}.pdf`
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          // Extraer el nombre del archivo (puede estar entre comillas)
          let extractedFilename = filenameMatch[1].replace(/['"]/g, '')
          
          // Limpiar el nombre del archivo (remover rutas como uploads\ o signed_documents\)
          extractedFilename = extractedFilename.replace(/^.*[\\\/]/, '')
          
          // Si hay un filename*=UTF-8'', usar ese
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/)
          if (utf8Match && utf8Match[1]) {
            try {
              extractedFilename = decodeURIComponent(utf8Match[1])
              // Limpiar también aquí
              extractedFilename = extractedFilename.replace(/^.*[\\\/]/, '')
            } catch (e) {
              console.warn('Error al decodificar filename UTF-8:', e)
            }
          }
          
          if (extractedFilename) {
            filename = extractedFilename
          }
        }
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      toast.success('Documento descargado exitosamente')
    } catch (error) {
      console.error('Error al descargar:', error)
      toast.error('Error al descargar: ' + error.message)
    }
  }

  // Si no es admin, no mostrar nada (ya se redirige en useEffect)
  if (!isAdmin) {
    return null
  }

  const claveConsulta = `${page}|${filters.documento}|${filters.propietario}`
  const datosListos = claveCargada === claveConsulta && !loading
  const desde = !datosListos || total === 0 ? 0 : ((page - 1) * PAGE_SIZE) + 1
  const hasta = !datosListos ? 0 : Math.min(page * PAGE_SIZE, total)

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-title">
              <h1>Historial General</h1>
              <p>Vista administrativa de TODOS los documentos del sistema</p>
            </div>
          </div>
          <div className="header-right">
            <div className="admin-view-banner" style={{background: '#dc2626', color: 'white', padding: '12px 20px', borderRadius: '8px', fontWeight: 'bold'}}>
              ⚠️ Vista de Administrador: {datosListos ? `${total} documento(s) ${hasActiveFilters ? 'coinciden con el filtro' : 'en total'}` : 'cargando...'}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Barra de acciones masivas */}
        {signatures.length > 0 && (
          <div className="mb-4 p-4 bg-white rounded-lg shadow flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedDocuments.length === signatures.length && signatures.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Seleccionar esta página ({selectedDocuments.length} seleccionados)
                </span>
              </label>
            </div>
            {selectedDocuments.length > 0 && (
              <button
                onClick={abrirModalEliminarMasivo}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 size={18} />
                <span>Eliminar {selectedDocuments.length} seleccionado(s)</span>
              </button>
            )}
          </div>
        )}

        {/* Filtros */}
        <div className="mb-6 p-4 bg-white rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filtros de Búsqueda</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-auto px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors flex items-center gap-1"
              >
                <X size={14} />
                Limpiar
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Documento
              </label>
              <input
                id="tour-news-historial-search"
                type="text"
                value={filters.documento}
                onChange={(e) => handleFilterChange('documento', e.target.value)}
                placeholder="Buscar por título o descripción..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrar por Propietario
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.propietario}
                  onChange={(e) => handleFilterChange('propietario', e.target.value)}
                  onFocus={() => {
                    setPropietarioInputFocused(true)
                    if (filters.propietario.trim().length > 0) {
                      setShowPropietarioSuggestions(true)
                    }
                  }}
                  onBlur={() => {
                    // Delay para permitir que el click en la lista se registre
                    setTimeout(() => {
                      setPropietarioInputFocused(false)
                      setShowPropietarioSuggestions(false)
                    }, 200)
                  }}
                  placeholder="Escribe correo o nombre del propietario..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                  disabled={loadingUsers}
                />
                
                {/* Lista desplegable de sugerencias */}
                {showPropietarioSuggestions && propietarioInputFocused && getFilteredUsers().length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {getFilteredUsers().map((user) => {
                      const displayText = `${user.email}${user.nombre || user.apellido ? ` (${user.nombre || ''} ${user.apellido || ''})`.trim() : ''}`
                      return (
                        <div
                          key={user.id}
                          onClick={() => handleSelectPropietario(user.email)}
                          className="px-4 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{user.email}</div>
                          {(user.nombre || user.apellido) && (
                            <div className="text-sm text-gray-500">{user.nombre || ''} {user.apellido || ''}</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
                
                {/* Mensaje cuando no hay resultados */}
                {showPropietarioSuggestions && propietarioInputFocused && getFilteredUsers().length === 0 && filters.propietario.trim().length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    No se encontraron usuarios
                  </div>
                )}
              </div>
              {filters.propietario && !showPropietarioSuggestions && (
                <div className="mt-1 text-xs text-gray-500">
                  {users.find(u => u.email.toLowerCase() === filters.propietario.toLowerCase()) 
                    ? `✓ Usuario encontrado: ${users.find(u => u.email.toLowerCase() === filters.propietario.toLowerCase())?.nombre || ''} ${users.find(u => u.email.toLowerCase() === filters.propietario.toLowerCase())?.apellido || ''}`.trim()
                    : 'Escribe para buscar usuarios'}
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-3 text-sm text-gray-600">
            {!datosListos ? 'Cargando...' : (total === 0 ? 'Sin resultados' : `Mostrando ${desde}-${hasta} de ${total} documento(s)`)}
          </div>
        </div>

        {/* Estadísticas por estado */}
        {datosListos && Object.keys(estadosCount).length > 0 && (
          <div className="mb-6 p-4 bg-white rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-3">Documentos por Estado:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(estadosCount).map(([estado, count]) => (
                <div key={estado} className="p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600 capitalize">{estado.replace('_', ' ')}</div>
                  <div className="text-2xl font-bold">{count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div id="tour-news-historial-list">
        {(loading || !datosListos) ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Cargando documentos...</p>
          </div>
        ) : signatures.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={64} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 text-lg">No hay documentos en el sistema</p>
          </div>
        ) : (
          <div className="space-y-4">
            {signatures.map((signature) => {
              const isSelected = selectedDocuments.includes(signature.id)
              const esPropietario = signature.owner_id === user?.id
              const estaPendiente = signature.estado?.toLowerCase() === 'pendiente'
              const estaEnProceso = signature.estado?.toLowerCase() === 'en_proceso'
              const estaCancelado = signature.estado?.toLowerCase() === 'cancelado'
              const estaCompletado = signature.estado?.toLowerCase() === 'completado'
              
              // Verificar si todos los firmantes están rechazados
              const todosRechazados = signature.firmantes?.every(f => 
                f.estado?.toLowerCase() === 'rechazado'
              ) || false

              // Admin puede eliminar CUALQUIER documento
              const mostrarBotonEliminar = true

              return (
                <div key={signature.id} className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectDocument(signature.id)}
                        className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText size={24} className="text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-800">
                          {signature.titulo || `Documento ${signature.id}`}
                        </h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          signature.tipo === 'enviado' ? 'bg-blue-100 text-blue-800' :
                          signature.tipo === 'recibido' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {signature.tipo === 'enviado' ? 'Enviado' :
                           signature.tipo === 'recibido' ? 'Recibido' :
                           signature.tipo === 'visualizador' ? 'Visualizador' : 'Todos'}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          estaPendiente ? 'bg-yellow-100 text-yellow-800' :
                          estaEnProceso ? 'bg-blue-100 text-blue-800' :
                          estaCompletado ? 'bg-green-100 text-green-800' :
                          estaCancelado ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {signature.estado || 'Desconocido'}
                        </span>
                      </div>
                      
                      {signature.descripcion && (
                        <p className="text-gray-600 mb-3">{signature.descripcion}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600">
                        {signature.fecha_creacion && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Creado:</span>
                            <span>{new Date(signature.fecha_creacion).toLocaleString('es-CO')}</span>
                          </div>
                        )}
                        {signature.owner_nombre && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Propietario:</span>
                            <span>{signature.owner_nombre} ({signature.owner_email})</span>
                          </div>
                        )}
                        {signature.firmantes && signature.firmantes.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Firmantes:</span>
                            <span>{signature.firmantes.length}</span>
                          </div>
                        )}
                        {signature.visualizadores && signature.visualizadores.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Eye size={16} />
                            <span className="font-medium">Visualizadores:</span>
                            <span>{signature.visualizadores.length}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>

                    <div className="flex gap-2">
                      {mostrarBotonEliminar && (
                        <button
                          onClick={() => abrirModalEliminar(signature.id, signature.estado)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                          title="Eliminar documento"
                        >
                          <Trash2 size={18} />
                          <span className="hidden md:inline">Eliminar</span>
                        </button>
                      )}
                      <BotonVisualizar documentoId={signature.id} />
                      <button
                        onClick={() => descargarDocumento(
                          signature.id,
                          signature.ruta_pdf_firmado,
                          signature.ruta_pdf_original,
                          signature.estado
                        )}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                        title="Descargar documento"
                      >
                        <Download size={18} />
                        <span className="hidden md:inline">Descargar</span>
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        </div>

        <div id="tour-news-historial-pagination">
        {datosListos && total > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
            <span className="text-sm text-gray-500">
              Página {page} de {Math.max(totalPages, 1)}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(actual => Math.max(1, actual - 1))}
                disabled={page <= 1 || loading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage(actual => actual + 1)}
                disabled={page >= totalPages || loading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Modal de confirmación de eliminación masiva */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-600" size={24} />
              <h3 className="text-lg font-semibold">Confirmar Eliminación Masiva</h3>
            </div>
            <p className="text-gray-700 mb-6">
              ¿Estás seguro de que deseas eliminar <strong>{selectedDocuments.length} documento(s)</strong>? 
              Esta acción no se puede deshacer y también eliminará los archivos físicos asociados.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false)
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={eliminarDocumentosMasivo}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar {selectedDocuments.length} documento(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-600" size={24} />
              <h3 className="text-lg font-semibold">Confirmar Eliminación</h3>
            </div>
            <p className="text-gray-700 mb-6">
              ¿Estás seguro de que deseas eliminar este documento? Esta acción no se puede deshacer.
              {documentoToDelete?.estado && (
                <span className="block mt-2 text-sm text-gray-600">
                  Estado actual: <strong>{documentoToDelete.estado}</strong>
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDocumentoToDelete(null)
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={eliminarDocumento}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HistorialGeneral
